/* eslint-disable jsx-a11y/media-has-caption */
import { Icon } from '@iconify/react';
import classNames from 'classnames';
import { cubicBezier, motion } from 'framer-motion';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import type { ReactElement } from 'react';
import { TypeAnimation } from 'react-type-animation';

import HomepageBackground from '@/assets/homepage_bg.svg';
import MacbookMockup2 from '@/assets/macbook_mockup_2.png';
import StepsBackground1 from '@/assets/steps_background_1.svg';
import StepsBackground2 from '@/assets/steps_background_2.svg';
import stepsAnimation from '@/assets/stepsAnimation.gif';
import CustomerStories from '@/components/AIHR/CustomerStories';
import Button from '@/components/Button';
import Chip from '@/components/Chip';
import Layout from '@/components/Landing/Layout';
import Text from '@/components/Text';
import { useProfile } from '@/hooks/useProfile';
import { createMetaTags } from '@/utils/seo';

export default function Home() {
  const { data } = useProfile();
  const advantages = [
    {
      icon: 'mdi:anonymous',
      title: 'Anonymize CVs',
      description:
        'Eliminate the risk of identifing Candidates by removing details from CVs, such as names, addresses, and photos.',
    },
    {
      icon: 'radix-icons:timer',
      title: 'Save Time',
      description:
        'Save your time and resources by focusing on the core qualifications and experience of candidates.',
    },
    {
      icon: 'icon-park-outline:people-safe-one',
      title: 'Protect Privacy',
      description:
        'Our tool ensures that all personal information is stripped from CVs, so candidates can trust that their data is in safe hands.',
    },
  ];
  const steps = [
    {
      name: 'Drag & Drop',
      time: '10 sec',
      description: (
        <Text variant="body-small">
          Simply <strong>upload</strong> your files and let <strong>AI</strong>{' '}
          do its job for you!
        </Text>
      ),
    },
    {
      name: 'Edit',
      time: '2 min',
      description: (
        <Text variant="body-small">
          Now it’s your turn to do the work! Check the scanned file,
          <br /> <strong>add</strong> what you’d like to it or{' '}
          <strong>remove</strong> stuff not needed.
        </Text>
      ),
    },
    {
      name: 'Download',
      time: '30 sec',
      description: (
        <Text variant="body-small">
          The files are ready! You’re good to go in less than 5 minutes!
        </Text>
      ),
    },
  ];

  const transition = { ease: cubicBezier(0.25, 0.1, 0.25, 1), duration: 1 };
  return (
    <>
      <Head>
        {createMetaTags({
          title: 'MindPal - Resume Generator',
          description:
            'Protect candidate data and speed up the recruitment process. resumes Anonymize Resumes Safeguarding candidate dataand streamlining your recruitment process. resumes Introduce Flexibility Into Your Work Stop using Canva or Photoshop to anonymize or edit resumes. You can use the Resume Generator not only to anonymize resumes, but also to make them clearer and highlight candidates',
        })}
      </Head>
      <main className="flex flex-1 flex-col overflow-auto">
        <div className="relative flex  min-h-screen flex-col px-5 pt-40 md:px-20">
          <div className="mt-20 flex flex-1 flex-col justify-center">
            <div className="flex flex-1">
              <div className="flex h-full flex-[3] flex-col self-center ">
                <div className="overflow-hidden py-3 lg:ml-28">
                  <motion.div
                    initial={{ y: 150 }}
                    animate={{ y: 0 }}
                    transition={transition}
                  >
                    <Text variant="body-small" as="h1">
                      Protect candidate data and speed up the <br /> recruitment
                      process.
                    </Text>
                  </motion.div>
                </div>
                <Text
                  variant="display-3"
                  className="mt-16 flex flex-col overflow-hidden text-h2 md:text-display-3"
                >
                  <motion.span
                    className="overflow-hidden"
                    transition={transition}
                    initial={{ y: 250 }}
                    animate={{ y: 0 }}
                  >
                    <TypeAnimation
                      speed={{ type: 'keyStrokeDelayInMs', value: 120 }}
                      preRenderFirstString
                      sequence={[
                        'Generate',
                        1250,
                        'Anonymize',
                        1250,
                        'Edit',
                        1250,
                      ]}
                      repeat={Infinity}
                    />
                    resumes
                  </motion.span>
                </Text>
                <Text
                  variant="display-3"
                  className="flex flex-col overflow-hidden text-h2 md:text-display-3"
                >
                  <motion.span
                    transition={transition}
                    initial={{ y: 750 }}
                    animate={{ y: 0 }}
                    className=" text-neutral-400"
                  >
                    with the power of AI
                  </motion.span>
                </Text>
              </div>
              <div className="relative -mr-20 mb-4  hidden flex-[2] flex-col overflow-hidden md:flex">
                <motion.div
                  initial={{ x: 250, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={transition}
                  className="flex-1"
                >
                  <Image
                    alt=""
                    src={MacbookMockup2}
                    fill
                    className="object-cover object-left"
                  />
                </motion.div>
              </div>
            </div>
          </div>
          <div className="absolute bottom-0 right-0 -z-20 h-full [&>svg]:h-full">
            <HomepageBackground />
          </div>
        </div>
        <div className="flex bg-neutral-200 px-5 py-32 md:px-20">
          <div className="flex flex-1 flex-col gap-7 self-center">
            <Text
              variant="h3"
              className="font-semibold text-neutral-900"
              as="h2"
            >
              Introduce Flexibility Into Your Work
            </Text>
            <Text className="text-neutral-700">
              Stop using Canva or Photoshop to anonymize or edit resumes. You
              can use the Resume Generator not only to anonymize resumes, but
              also to make them clearer and highlight candidates strengths.
            </Text>
            <Link
              tabIndex={-1}
              href={data ? '/dashboard' : '/dashboard/auth/signin'}
              className="mb-8 w-fit"
            >
              <Button className="self-start" size="long">
                Try now
              </Button>
            </Link>
            <div className="flex flex-col gap-5">
              {advantages.map(({ description, icon, title }) => (
                <div key={title} className="flex gap-5">
                  <div>
                    <Icon
                      icon={icon}
                      className="text-[2rem] text-neutral-700"
                    />
                  </div>
                  <div className="flex flex-[5] flex-col gap-3">
                    <Text
                      variant="h5"
                      className="font-semibold text-neutral-900"
                    >
                      {title}
                    </Text>
                    <Text variant="body-small" className="text-neutral-700">
                      {description}
                    </Text>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative hidden flex-1 items-center justify-center bg-resume-background bg-contain bg-center bg-no-repeat lg:flex">
            <div className="flex w-full items-center justify-center">
              <div className="w-full p-[9%]">
                <video
                  src="https://mindpalstorage.blob.core.windows.net/public-files/assets/intro-cv-gen-3.mp4"
                  className="aspect-video w-full"
                  controls
                />
              </div>
            </div>
          </div>
        </div>
        <div className="relative flex px-5 py-32 md:px-20">
          <div className="flex-[2]">
            <Text variant="display-3" className="mb-10" as="h2">
              Just 3 simple steps
            </Text>
            <Link
              tabIndex={-1}
              href={data ? '/dashboard' : '/dashboard/auth/signin'}
            >
              <Button size="long">Try now</Button>
            </Link>
            <div className="mt-16 flex max-w-xl flex-col gap-5">
              {steps.map(({ description, name, time }, index) => (
                <div
                  key={name}
                  className={classNames(
                    'flex w-fit flex-col gap-2 rounded-[1.25rem] bg-secondary-500/20 p-5',
                    {
                      'self-end': index % 2 !== 0,
                    }
                  )}
                >
                  <Text variant="body-caption" className="text-neutral-300">
                    STEP 0{index + 1}
                  </Text>
                  <div className="mb-4 flex items-center gap-5">
                    <Text>{name}</Text>
                    <Chip variant="primary">
                      <>
                        <Icon icon="uiw:time-o" />
                        {time}
                      </>
                    </Chip>
                  </div>

                  {description}
                </div>
              ))}
            </div>
            <div className="absolute bottom-0 left-0 -z-20 h-full w-full overflow-hidden [&>svg]:h-full">
              <StepsBackground1 />
            </div>
            <div className="absolute bottom-0 right-0 -z-20 h-full [&>svg]:h-full">
              <StepsBackground2 />
            </div>
          </div>
          <div className="hidden flex-[2] flex-col lg:flex">
            <div className="max-w-2xl self-end overflow-hidden rounded-[20px] border border-neutral-800">
              <Image src={stepsAnimation} alt="" />
            </div>
          </div>
        </div>
        <CustomerStories />
        <div className="flex flex-col items-center gap-10 bg-gradient-to-r from-black to-secondary-900 px-5 py-56 md:px-20">
          <Text
            variant="h1"
            className="text-center text-h4 font-semibold md:text-h1"
          >
            What are you waiting for?
          </Text>
          <Text
            variant="h5"
            className="text-center font-semibold text-neutral-200"
          >
            Start simplifing your workflow today and sign up now!
          </Text>
          <Link
            tabIndex={-1}
            href={data ? '/dashboard' : '/dashboard/auth/signin'}
          >
            <Button className="self-center">Start a free trial</Button>
          </Link>
        </div>
      </main>
    </>
  );
}

Home.getLayout = (page: ReactElement) => <Layout>{page}</Layout>;
